import type { SceneAsset } from "@/types"
import type { ClipLayout, ScriptContent } from "./constants"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { useEstimatedProgress } from "@/hooks/use-estimated-progress"

export default function TimelineTrackImage({
  clips,
  imageUrls,
  content,
  brandColour,
  sceneAssets,
  sceneAssetStatuses,
  selectedIndex,
  onSelect,
}: {
  clips: ClipLayout[]
  imageUrls: string[]
  content: ScriptContent | undefined
  brandColour: string
  sceneAssets: SceneAsset[]
  sceneAssetStatuses: Record<string, { status: SceneAsset["status"]; message?: string; progress?: number }>
  selectedIndex: number
  onSelect: (index: number) => void
}) {
  const titleBgColour = content?.title_card_bg_colour || brandColour

  return (
    <div className="relative flex h-14">
      {clips.map((clip) => {
        const selectIndex = clip.type === "title" ? -1 : clip.index
        return (
          <ImageClip
            key={`img-${clip.index}-${clip.type}`}
            clip={clip}
            imageUrl={clip.type === "content" ? imageUrls[clip.index] : undefined}
            titleBgColour={titleBgColour}
            closingBgColour={content?.closing_bg_colour || brandColour}
            sceneAssets={sceneAssets}
            sceneAssetStatuses={sceneAssetStatuses}
            isSelected={selectedIndex === selectIndex}
            onSelect={() => onSelect(selectIndex)}
          />
        )
      })}
    </div>
  )
}

function ImageClip({
  clip,
  imageUrl,
  titleBgColour,
  closingBgColour,
  sceneAssets,
  sceneAssetStatuses,
  isSelected,
  onSelect,
}: {
  clip: ClipLayout
  imageUrl: string | undefined
  titleBgColour: string
  closingBgColour: string
  sceneAssets: SceneAsset[]
  sceneAssetStatuses: Record<string, { status: SceneAsset["status"]; message?: string; progress?: number }>
  isSelected: boolean
  onSelect: () => void
}) {
  const isContent = clip.type === "content"

  const enhanceKey = `${clip.index}-enhanced_image`
  const animateKey = `${clip.index}-animated_clip`
  const enhanceStatus = isContent ? sceneAssetStatuses[enhanceKey]?.status : undefined
  const animateStatus = isContent ? sceneAssetStatuses[animateKey]?.status : undefined
  const enhancedAsset = isContent
    ? sceneAssets.find((sa) => sa.scene_index === clip.index && sa.asset_type === "enhanced_image")
    : undefined
  const animatedAsset = isContent
    ? sceneAssets.find((sa) => sa.scene_index === clip.index && sa.asset_type === "animated_clip")
    : undefined

  const isEnhancing = enhanceStatus === "generating"
  const isAnimating = animateStatus === "generating"
  const isEnhanced = enhancedAsset?.status === "completed" && !!enhancedAsset?.file_url
  const isAnimated = animatedAsset?.status === "completed" && !!animatedAsset?.file_url
  const processing = isEnhancing || isAnimating

  // Stale check — Kling 2.6 Pro quantises duration to 5 or 10 seconds
  const actual = animatedAsset?.target_duration
  const contentScenes = clip.type === "content" ? clip.duration : 0
  const expected = isContent ? (Math.round(contentScenes) <= 7 ? 5 : 10) : null
  const isStale = isAnimated && actual != null && expected != null && actual !== expected

  const thumbnailUrl = enhancedAsset?.file_url || imageUrl

  const enhanceProgress = useEstimatedProgress({
    isActive: isEnhancing,
    serverProgress: sceneAssetStatuses[enhanceKey]?.progress,
    estimatedDuration: 25,
  })
  const animateProgress = useEstimatedProgress({
    isActive: isAnimating,
    serverProgress: sceneAssetStatuses[animateKey]?.progress,
    estimatedDuration: 90,
  })
  const activeProgress = isAnimating ? animateProgress : enhanceProgress

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`absolute top-0.5 bottom-0.5 overflow-hidden rounded transition-all ${
        isSelected
          ? "ring-2 ring-red/30"
          : "ring-1 ring-stone/20 hover:ring-stone/40"
      }`}
      style={{ left: clip.left, width: clip.width }}
    >
      {/* Thumbnail for content scenes */}
      {isContent && thumbnailUrl && (
        <OptimizedImage
          src={thumbnailUrl}
          alt={`Scene ${clip.index + 1}`}
          className="size-full object-cover"
        />
      )}

      {/* Title clip */}
      {clip.type === "title" && (
        <div className="flex size-full items-center justify-center rounded-[3px]" style={{ backgroundColor: titleBgColour }}>
          <span className="text-[9px] font-semibold text-white/80">Title</span>
        </div>
      )}

      {/* Closing clip */}
      {clip.type === "closing" && (
        <div className="flex size-full items-center justify-center gap-1 rounded-[3px]" style={{ backgroundColor: closingBgColour }}>
          <span className="text-[9px] font-medium text-white/80">Close</span>
        </div>
      )}

      {/* Status badges */}
      {isContent && (isEnhanced || isAnimated) && !processing && (
        <div className="absolute left-0.5 top-0.5 flex gap-0.5">
          {isEnhanced && (
            <span className="rounded bg-red/90 px-0.5 py-px text-[7px] font-bold leading-tight text-white">
              HD
            </span>
          )}
          {isAnimated && (
            <span className={`rounded px-0.5 py-px text-[7px] font-bold leading-tight text-white ${isStale ? "bg-amber-500/90" : "bg-indigo-600/90"}`}>
              {actual ?? 4}s
            </span>
          )}
        </div>
      )}

      {/* Processing overlay */}
      {processing && (
        <>
          <div className="absolute inset-x-0 top-0 z-10 h-0.5 bg-charcoal/20">
            <div
              className="h-full bg-red transition-all duration-300"
              style={{ width: `${activeProgress.progress * 100}%` }}
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-charcoal/50">
            <svg className="size-3.5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        </>
      )}

      {/* Scene number */}
      {isContent && !processing && (
        <div className="absolute right-0.5 top-0.5 flex size-4 items-center justify-center rounded-full bg-charcoal/70 text-[7px] font-bold text-white">
          {clip.index + 1}
        </div>
      )}
    </button>
  )
}
